/**
 * Admin → Posting results.
 *
 * What happened to each opportunity broadcast: how many were mailed, how many
 * came back to the app from the link, how many applied afterwards.
 *
 * Deliberately a table and not a funnel chart. Three or four numbers per row,
 * a dozen rows — a chart would spend a lot of ink to say what the digits
 * already say, and would invite reading a trend into what is really a handful
 * of individually-decided sends.
 *
 * THERE IS NO OPEN RATE. Counting opens needs a tracking pixel: corporate mail
 * clients block remote images by default, so it would be low and biased in no
 * measurable direction, and it is behavioural tracking of named employees —
 * a privacy question rather than an engineering one. Every column here is a
 * fact the app records about its own surfaces. The page says so, because a
 * missing column silently reads as an oversight.
 */

import { useState } from 'react'
import { ErrorCard } from '../components/ErrorCard'
import {
  Badge,
  Body1,
  Caption1,
  Dropdown,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Link } from 'react-router-dom'
import { useBroadcastFunnel, type BroadcastFunnelRow } from '../hooks/useAdminUsers'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  toolbar: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  note: {
    padding: '10px 14px',
    borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
    background: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
  },
  num: { fontVariantNumeric: 'tabular-nums', textAlign: 'right' as const },
  muted: { color: tokens.colorNeutralForeground3 },
  title: { display: 'block', fontWeight: tokens.fontWeightSemibold },
})

const EVENT_LABEL: Record<string, string> = {
  'demand.posted': 'Posted',
  'demand.closing_soon': 'Closing soon',
}

/** "12 (40%)" — the share is of what was actually delivered, not of what was
 * queued: a send that failed never had a chance to convert, and counting it in
 * the denominator quietly blames the recipient for a delivery problem. */
function withShare(n: number, base: number): string {
  if (!base) return String(n)
  return `${n} (${Math.round((n / base) * 100)}%)`
}

function Row({ r }: { r: BroadcastFunnelRow }) {
  const styles = useStyles()
  const when = r.first_sent ? new Date(r.first_sent).toLocaleDateString() : '—'
  return (
    <TableRow>
      <TableCell>
        <Link to={`/demand/${r.demand_id}`} className={styles.title}>
          {r.title ?? `Demand ${r.demand_id}`}
        </Link>
        <Caption1 className={styles.muted}>
          {r.slug ?? `#${r.demand_id}`} · {r.focus_area_code ?? 'DSAI'} · {when}
        </Caption1>
      </TableCell>
      <TableCell>
        <Badge appearance="tint" color="informative">
          {EVENT_LABEL[r.event] ?? r.event}
        </Badge>
      </TableCell>
      <TableCell className={styles.num}>{r.sent}</TableCell>
      <TableCell className={styles.num}>
        {r.failed > 0 ? (
          <Tooltip content="Delivery failed after retries — see the Email log" relationship="label">
            <Badge appearance="tint" color="danger">
              {r.failed}
            </Badge>
          </Tooltip>
        ) : (
          <span className={styles.muted}>0</span>
        )}
      </TableCell>
      <TableCell className={styles.num}>{withShare(r.arrived, r.sent)}</TableCell>
      <TableCell className={styles.num}>{withShare(r.applied, r.sent)}</TableCell>
    </TableRow>
  )
}

export function AdminBroadcastFunnel() {
  const styles = useStyles()
  const [days, setDays] = useState(90)
  const { data, isLoading, isError, refetch } = useBroadcastFunnel(days)
  const rows = data?.items ?? []

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <Title2>Posting results</Title2>
      </div>

      <div className={styles.toolbar}>
        <Dropdown
          value={`Last ${days} days`}
          selectedOptions={[String(days)]}
          onOptionSelect={(_e, d) => setDays(Number(d.optionValue))}
          style={{ minWidth: '160px' }}
        >
          <Option value="30">Last 30 days</Option>
          <Option value="90">Last 90 days</Option>
          <Option value="365">Last 365 days</Option>
        </Dropdown>
        {isLoading && <Spinner size="tiny" />}
      </div>

      <div className={styles.note}>
        <strong>Arrived</strong>: followed the mail&apos;s link (a floor, not a rate).{' '}
        <strong>Applied</strong>: applied after the send, by any route. No open rate: that needs
        a tracking pixel.
      </div>

      {isError ? (
        <ErrorCard title="Could not load posting results" message="Please try again." retry={() => void refetch()} />
      ) : !isLoading && rows.length === 0 ? (
        <Body1 className={styles.muted}>No opportunity broadcasts in this period.</Body1>
      ) : (
        <Table aria-label="Posting results" size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '42%' }}>Demand</TableHeaderCell>
              <TableHeaderCell style={{ width: '12%' }}>Mail</TableHeaderCell>
              <TableHeaderCell style={{ width: '10%', textAlign: 'right' }}>Sent</TableHeaderCell>
              <TableHeaderCell style={{ width: '10%', textAlign: 'right' }}>Failed</TableHeaderCell>
              <TableHeaderCell style={{ width: '13%', textAlign: 'right' }}>Arrived</TableHeaderCell>
              <TableHeaderCell style={{ width: '13%', textAlign: 'right' }}>Applied</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <Row key={`${r.demand_id}-${r.event}`} r={r} />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
